import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { DialogActions, DialogBody, DialogTitle, DialogTrigger, makeStyles, mergeClasses } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { FixedPriceMutationDto, FixedPriceOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { FormikConfig, useFormikContext } from 'formik'
import {
    bundleIcon,
    DeleteRegular,
    DismissFilled,
    DismissRegular,
    SaveEditFilled,
    SaveEditRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import FixedPriceMutationDialogBodyContent from './FixedPriceMutationDialogBodyContent'
import TooltippedButton from '../../Button/TooltippedButton'
import { useCounter } from 'usehooks-ts'

const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const SaveAndNewIcon = bundleIcon(SaveEditFilled, SaveEditRegular)

const useStyles = makeStyles({
    actions: {
        width: '100%',
    },
})

export default function FixedPriceMutationDialogBody(
    props: HammockFormDialogProps<FixedPriceMutationDto, BaseMutableOverviewDialogProps<FixedPriceOverviewDto>>,
): ReactElement {
    const axios = useAxios()

    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const { t: translate } = useTranslation()

    const [submitVariant, setSubmitVariant] = useState<'save' | 'saveAndNew'>('save')
    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)
    const { initialValues, submitForm, isSubmitting, resetForm } = useFormikContext<FixedPriceMutationDto>()

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<FixedPriceOverviewDto>), [props])

    const handleSubmit = useCallback<FormikConfig<FixedPriceMutationDto>['onSubmit']>(
        values =>
            dialogBodyProps.id
                ? axios.put(`/registration/fixed-price/${dialogBodyProps.id}`, values)
                : axios
                      .post(`/registration/fixed-price`, values)
                      .then(() => {
                          if (submitVariant === 'saveAndNew') {
                              resetForm({
                                  values: {
                                      ...initialValues,
                                      vatType: values.vatType,
                                      currency: values.currency,
                                      date: values.date,
                                      userId: values.userId,
                                  },
                              })
                              updateFocusTrigger()
                          } else {
                              resetForm()
                          }
                      })
                      .then(() => submitVariant === 'saveAndNew'),
        [submitVariant],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    const handleDelete = () =>
        axios
            .delete(`/registration/fixed-price/${dialogBodyProps.id}`)
            .then(() => resetForm())
            .then(() => props.onDismiss())
    const handleCancel = () => {
        resetForm()
        props.onDismiss()
    }

    useEffect(() => updateFocusTrigger(), [])

    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    return (
        <DialogBody>
            <DialogTitle>{dialogBodyProps.id ? translate('fixedprice.edit.dialog.edit') : translate('fixedprice.edit.dialog.create')}</DialogTitle>
            <FixedPriceMutationDialogBodyContent {...props} focusTrigger={focusTrigger} updateFocusTrigger={updateFocusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    {dialogBodyProps.id && (
                        <TooltippedButton
                            appearance="subtle"
                            display="iconOnly"
                            label={translate('action.delete')}
                            icon={<DeleteRegular />}
                            onClick={() => handleDelete()}
                            disabled={isSubmitting || dialogBodyProps.item?.invoice?.status !== undefined}
                        />
                    )}
                    {!dialogBodyProps.id && (
                        <TooltippedButton
                            appearance="subtle"
                            label={translate('action.saveandnew')}
                            icon={<SaveAndNewIcon />}
                            onClick={() => {
                                setSubmitVariant('saveAndNew')
                                submitForm()
                            }}
                            disabled={isSubmitting || isInvoiceFinal}
                        />
                    )}
                    {!dialogBodyProps.id && <div></div>}
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <TooltippedButton
                            appearance="primary"
                            label={translate('action.save')}
                            icon={<SaveIcon />}
                            onClick={() => {
                                setSubmitVariant('save')
                                submitForm()
                            }}
                            disabled={isSubmitting || isInvoiceFinal}
                        />
                        <DialogTrigger disableButtonEnhancement>
                            <TooltippedButton
                                appearance="secondary"
                                label={translate('action.cancel')}
                                icon={<CloseIcon />}
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            />
                        </DialogTrigger>
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
